import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  Alert,
  Tabs,
  Tab,
  FormControlLabel,
  Checkbox,
  LinearProgress,
  Collapse,
  IconButton,
} from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import CancelIcon from '@mui/icons-material/Cancel';
import RefreshIcon from '@mui/icons-material/Refresh';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { useNotify, usePermissions } from 'react-admin';
import { apiGet, apiPost, ApiClientError } from '../utils';

interface ScraperJob {
  id: string;
  job_type: string;
  status: string;
  params: Record<string, unknown>;
  progress: {
    current: number;
    total: number;
    successfulMovies: number;
    failedMovies: number;
    currentYear?: number;
    message?: string;
  } | null;
  result: {
    moviesStored: number;
    songsStored: number;
    fixed?: number;
    skipped?: number;
    errors: string[];
  } | null;
  error_message: string | null;
  cancelled: boolean;
  created_at: string;
  started_at: string | null;
  completed_at: string | null;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel = ({ children, value, index, ...other }: TabPanelProps) => (
  <div role="tabpanel" hidden={value !== index} {...other}>
    {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
  </div>
);

const STATUS_COLORS: Record<string, 'default' | 'primary' | 'success' | 'error' | 'warning'> = {
  pending: 'default',
  running: 'primary',
  completed: 'success',
  failed: 'error',
  cancelled: 'warning',
};

const JOB_TYPE_LABELS: Record<string, string> = {
  language_scrape: 'Language Scrape',
  american_scrape: 'American Films',
  category_scrape: 'Category Page',
  single_url: 'Single URL',
  batch_urls: 'Batch URLs',
  fix_lyricists: 'Fix Lyricists',
};

const formatDate = (dateStr: string | null) => {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleString();
};

const formatDuration = (start: string | null, end: string | null) => {
  if (!start) return '—';
  const startMs = new Date(start).getTime();
  const endMs = end ? new Date(end).getTime() : Date.now();
  const seconds = Math.floor((endMs - startMs) / 1000);
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  return `${minutes}m ${remainingSeconds}s`;
};

// ==================== JOB DETAIL ROW ====================

const JobDetailRow = ({ job }: { job: ScraperJob }) => (
  <Box sx={{ p: 2, bgcolor: 'grey.50' }}>
    <Typography variant="subtitle2" gutterBottom>Parameters</Typography>
    <pre style={{ margin: 0, fontSize: '0.8rem', whiteSpace: 'pre-wrap' }}>
      {JSON.stringify(job.params, null, 2)}
    </pre>

    {job.progress && job.progress.total > 0 && (
      <Box sx={{ mt: 2 }}>
        <Typography variant="subtitle2" gutterBottom>Progress</Typography>
        <LinearProgress
          variant="determinate"
          value={(job.progress.current / job.progress.total) * 100}
          sx={{ mb: 1 }}
        />
        <Typography variant="body2" color="text.secondary">
          {job.progress.message} — {job.progress.current}/{job.progress.total}
          {job.progress.currentYear && ` (Year: ${job.progress.currentYear})`}
          {' | '}Stored: {job.progress.successfulMovies}, Failed: {job.progress.failedMovies}
        </Typography>
      </Box>
    )}

    {job.result && (
      <Box sx={{ mt: 2 }}>
        <Typography variant="subtitle2" gutterBottom>Result</Typography>
        <Typography variant="body2">
          Movies stored: {job.result.moviesStored}, Songs stored: {job.result.songsStored}
          {job.result.fixed !== undefined && `, Fixed: ${job.result.fixed}`}
          {job.result.skipped !== undefined && `, Skipped: ${job.result.skipped}`}
        </Typography>
        {job.result.errors.length > 0 && (
          <Alert severity="warning" sx={{ mt: 1 }}>
            {job.result.errors.length} error(s):
            <ul style={{ margin: '4px 0', paddingLeft: 20 }}>
              {job.result.errors.slice(0, 10).map((e, i) => (
                <li key={i} style={{ fontSize: '0.8rem' }}>{e}</li>
              ))}
              {job.result.errors.length > 10 && (
                <li style={{ fontSize: '0.8rem' }}>...and {job.result.errors.length - 10} more</li>
              )}
            </ul>
          </Alert>
        )}
      </Box>
    )}

    {job.error_message && (
      <Alert severity="error" sx={{ mt: 2 }}>{job.error_message}</Alert>
    )}

    <Box sx={{ mt: 2 }}>
      <Typography variant="body2" color="text.secondary">
        Created: {formatDate(job.created_at)}
        {job.started_at && ` | Started: ${formatDate(job.started_at)}`}
        {job.completed_at && ` | Completed: ${formatDate(job.completed_at)}`}
        {' | '}Duration: {formatDuration(job.started_at, job.completed_at)}
      </Typography>
    </Box>
  </Box>
);

// ==================== MAIN COMPONENT ====================

export const ScraperJobs = () => {
  const notify = useNotify();
  const { permissions } = usePermissions();

  const [activeTab, setActiveTab] = useState(0);
  const [jobs, setJobs] = useState<ScraperJob[]>([]);
  const [loading, setLoading] = useState(false);
  const [expandedJobId, setExpandedJobId] = useState<string | null>(null);

  // Create job state
  const [jobType, setJobType] = useState<string>('language_scrape');
  const [languages, setLanguages] = useState<string[]>([]);
  const [language, setLanguage] = useState('');
  const [yearLimit, setYearLimit] = useState('');
  const [maxMoviesPerYear, setMaxMoviesPerYear] = useState('');
  const [yearStart, setYearStart] = useState('');
  const [yearEnd, setYearEnd] = useState('');
  const [maxMovies, setMaxMovies] = useState('');
  const [singleUrl, setSingleUrl] = useState('');
  const [batchUrls, setBatchUrls] = useState('');
  const [category, setCategory] = useState('');
  const [categoryUrl, setCategoryUrl] = useState('');
  const [skipExisting, setSkipExisting] = useState(true);
  const [dryRun, setDryRun] = useState(false);
  const [fixLimit, setFixLimit] = useState('');
  const [creating, setCreating] = useState(false);

  const fetchJobs = useCallback(async () => {
    try {
      const data = await apiGet<ScraperJob[]>('/admin/api/scraper-jobs', {
        sort: '["created_at","DESC"]',
        range: '[0,49]',
      });
      setJobs(data);
    } catch (err) {
      console.error('Failed to fetch jobs:', err);
    }
  }, []);

  const fetchLanguages = useCallback(async () => {
    try {
      const data = await apiGet<string[]>('/admin/api/scraper-jobs/languages');
      setLanguages(data);
      setLanguage((prev) => prev || (data.length > 0 ? data[0] : ''));
    } catch {
      // Non-critical
    }
  }, []);

  // Initial load
  useEffect(() => {
    fetchJobs();
    fetchLanguages();
  }, [fetchJobs, fetchLanguages]);

  // Polling: 3s when active jobs exist, 15s otherwise
  const hasActiveJob = jobs.some((j) => j.status === 'running' || j.status === 'pending');

  useEffect(() => {
    const interval = hasActiveJob ? 3000 : 15000;
    const id = setInterval(fetchJobs, interval);
    return () => clearInterval(id);
  }, [hasActiveJob, fetchJobs]);

  const handleCancel = async (jobId: string) => {
    try {
      await apiPost(`/admin/api/scraper-jobs/${jobId}/cancel`);
      notify('Job cancellation requested', { type: 'info' });
      fetchJobs();
    } catch (err) {
      notify(
        err instanceof ApiClientError ? err.message : 'Failed to cancel job',
        { type: 'error' }
      );
    }
  };

  const handleCreateJob = async () => {
    setCreating(true);
    try {
      let params: Record<string, unknown>;

      switch (jobType) {
        case 'language_scrape':
          params = {
            jobType: 'language_scrape',
            language,
            ...(yearLimit && { yearLimit: parseInt(yearLimit) }),
            ...(maxMoviesPerYear && { maxMoviesPerYear: parseInt(maxMoviesPerYear) }),
            ...(yearStart && { yearStart: parseInt(yearStart) }),
            ...(yearEnd && { yearEnd: parseInt(yearEnd) }),
          };
          break;
        case 'american_scrape':
          params = {
            jobType: 'american_scrape',
            ...(yearStart && { yearStart: parseInt(yearStart) }),
            ...(yearEnd && { yearEnd: parseInt(yearEnd) }),
            ...(maxMovies && { maxMovies: parseInt(maxMovies) }),
          };
          break;
        case 'category_scrape':
          params = {
            jobType: 'category_scrape',
            categoryUrl,
            category,
            ...(maxMovies && { maxMovies: parseInt(maxMovies) }),
            skipExisting,
          };
          break;
        case 'single_url':
          params = {
            jobType: 'single_url',
            url: singleUrl,
            ...(category && { category }),
          };
          break;
        case 'batch_urls':
          params = {
            jobType: 'batch_urls',
            urls: batchUrls.split('\n').map((u) => u.trim()).filter(Boolean),
            ...(category && { category }),
          };
          break;
        case 'fix_lyricists':
          params = {
            jobType: 'fix_lyricists',
            dryRun,
            ...(fixLimit && { limit: parseInt(fixLimit) }),
            ...(category && { category }),
          };
          break;
        default:
          notify('Invalid job type', { type: 'error' });
          return;
      }

      await apiPost('/admin/api/scraper-jobs', params);
      notify('Job created and queued', { type: 'success' });
      setActiveTab(0);
      fetchJobs();
    } catch (err) {
      notify(
        err instanceof ApiClientError ? err.message : 'Failed to create job',
        { type: 'error' }
      );
    } finally {
      setCreating(false);
    }
  };

  const isSuperAdmin = permissions?.role === 'super_admin';
  if (!isSuperAdmin) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning">Scraper jobs are only available to super administrators.</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h5" gutterBottom>Scraper Jobs</Typography>
      <Card>
        <CardContent>
          <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)}>
            <Tab label="Job Queue" />
            <Tab label="Create Job" />
          </Tabs>

          {/* ==================== TAB 1: JOB QUEUE ==================== */}
          <TabPanel value={activeTab} index={0}>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
              <Button
                startIcon={<RefreshIcon />}
                onClick={() => { setLoading(true); fetchJobs().finally(() => setLoading(false)); }}
                disabled={loading}
                size="small"
              >
                Refresh
              </Button>
            </Box>

            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell width={40} />
                    <TableCell>Type</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Progress</TableCell>
                    <TableCell>Created</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {jobs.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={6} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                        No scraper jobs yet. Create one from the "Create Job" tab.
                      </TableCell>
                    </TableRow>
                  )}
                  {jobs.map((job) => {
                    const isExpanded = expandedJobId === job.id;
                    return (
                      <React.Fragment key={job.id}>
                        <TableRow
                          hover
                          sx={{ cursor: 'pointer', '& > td': { borderBottom: isExpanded ? 'none' : undefined } }}
                          onClick={() => setExpandedJobId(isExpanded ? null : job.id)}
                        >
                          <TableCell>
                            <IconButton size="small">
                              {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                            </IconButton>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={JOB_TYPE_LABELS[job.job_type] || job.job_type}
                              size="small"
                              variant="outlined"
                            />
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={job.status}
                              size="small"
                              color={STATUS_COLORS[job.status] || 'default'}
                            />
                            {job.status === 'running' && (
                              <CircularProgress size={14} sx={{ ml: 1 }} />
                            )}
                          </TableCell>
                          <TableCell>
                            {job.progress && job.progress.total > 0 ? (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <LinearProgress
                                  variant="determinate"
                                  value={(job.progress.current / job.progress.total) * 100}
                                  sx={{ flex: 1, minWidth: 80 }}
                                />
                                <Typography variant="body2" sx={{ whiteSpace: 'nowrap' }}>
                                  {job.progress.current}/{job.progress.total}
                                </Typography>
                              </Box>
                            ) : job.status === 'running' ? (
                              <Typography variant="body2" color="text.secondary">Starting...</Typography>
                            ) : (
                              '—'
                            )}
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">{formatDate(job.created_at)}</Typography>
                          </TableCell>
                          <TableCell onClick={(e) => e.stopPropagation()}>
                            {(job.status === 'running' || job.status === 'pending') && (
                              <Button
                                size="small"
                                color="error"
                                startIcon={<CancelIcon />}
                                onClick={() => handleCancel(job.id)}
                              >
                                Cancel
                              </Button>
                            )}
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell colSpan={6} sx={{ p: 0, border: 'none' }}>
                            <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                              <JobDetailRow job={job} />
                            </Collapse>
                          </TableCell>
                        </TableRow>
                      </React.Fragment>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </TabPanel>

          {/* ==================== TAB 2: CREATE JOB ==================== */}
          <TabPanel value={activeTab} index={1}>
            <Box sx={{ maxWidth: 600 }}>
              <FormControl fullWidth sx={{ mb: 3 }}>
                <InputLabel>Job Type</InputLabel>
                <Select
                  value={jobType}
                  label="Job Type"
                  onChange={(e) => setJobType(e.target.value)}
                >
                  <MenuItem value="language_scrape">Language Scrape (Wikipedia)</MenuItem>
                  <MenuItem value="american_scrape">American Films (Wikidata)</MenuItem>
                  <MenuItem value="category_scrape">Category Page (broader coverage)</MenuItem>
                  <MenuItem value="single_url">Single URL</MenuItem>
                  <MenuItem value="batch_urls">Batch URLs</MenuItem>
                  <MenuItem value="fix_lyricists">Fix Missing Lyricists</MenuItem>
                </Select>
              </FormControl>

              {/* Language Scrape Fields */}
              {jobType === 'language_scrape' && (
                <>
                  <FormControl fullWidth sx={{ mb: 2 }}>
                    <InputLabel>Language</InputLabel>
                    <Select value={language} label="Language" onChange={(e) => setLanguage(e.target.value)}>
                      {languages.map((lang) => (
                        <MenuItem key={lang} value={lang}>{lang}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                    <TextField
                      label="Year Start"
                      type="number"
                      value={yearStart}
                      onChange={(e) => setYearStart(e.target.value)}
                      size="small"
                      fullWidth
                    />
                    <TextField
                      label="Year End"
                      type="number"
                      value={yearEnd}
                      onChange={(e) => setYearEnd(e.target.value)}
                      size="small"
                      fullWidth
                    />
                  </Box>
                  <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                    <TextField
                      label="Year Limit"
                      type="number"
                      value={yearLimit}
                      onChange={(e) => setYearLimit(e.target.value)}
                      helperText="Max years to scrape"
                      size="small"
                      fullWidth
                    />
                    <TextField
                      label="Max Movies/Year"
                      type="number"
                      value={maxMoviesPerYear}
                      onChange={(e) => setMaxMoviesPerYear(e.target.value)}
                      helperText="Limit per year page"
                      size="small"
                      fullWidth
                    />
                  </Box>
                </>
              )}

              {/* American Scrape Fields */}
              {jobType === 'american_scrape' && (
                <>
                  <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                    <TextField
                      label="Year Start"
                      type="number"
                      value={yearStart}
                      onChange={(e) => setYearStart(e.target.value)}
                      size="small"
                      fullWidth
                    />
                    <TextField
                      label="Year End"
                      type="number"
                      value={yearEnd}
                      onChange={(e) => setYearEnd(e.target.value)}
                      size="small"
                      fullWidth
                    />
                  </Box>
                  <TextField
                    label="Max Movies"
                    type="number"
                    value={maxMovies}
                    onChange={(e) => setMaxMovies(e.target.value)}
                    helperText="Total movie limit"
                    size="small"
                    fullWidth
                    sx={{ mb: 2 }}
                  />
                </>
              )}

              {/* Category Scrape Fields */}
              {jobType === 'category_scrape' && (
                <>
                  <TextField
                    label="Category Page URL"
                    value={categoryUrl}
                    onChange={(e) => setCategoryUrl(e.target.value)}
                    fullWidth
                    sx={{ mb: 2 }}
                    placeholder="https://en.wikipedia.org/wiki/Category:1999_American_films"
                    helperText="Wikipedia category page URL"
                  />
                  <TextField
                    label="Category"
                    value={category}
                    onChange={(e) => setCategory(e.target.value)}
                    fullWidth
                    sx={{ mb: 2 }}
                    helperText="e.g. tamil, hindi, english. Determines scraper pipeline (Wikidata for english/american, Wikipedia for others)."
                  />
                  <TextField
                    label="Max Movies"
                    type="number"
                    value={maxMovies}
                    onChange={(e) => setMaxMovies(e.target.value)}
                    helperText="Limit movies to scrape (leave empty for all)"
                    size="small"
                    fullWidth
                    sx={{ mb: 2 }}
                  />
                  <FormControlLabel
                    control={<Checkbox checked={skipExisting} onChange={(e) => setSkipExisting(e.target.checked)} />}
                    label="Skip movies already in database (faster for backfill)"
                    sx={{ mb: 2 }}
                  />
                </>
              )}

              {/* Single URL Fields */}
              {jobType === 'single_url' && (
                <>
                  <TextField
                    label="Wikipedia/Wikidata URL"
                    value={singleUrl}
                    onChange={(e) => setSingleUrl(e.target.value)}
                    fullWidth
                    sx={{ mb: 2 }}
                    placeholder="https://en.wikipedia.org/wiki/..."
                  />
                  <TextField
                    label="Category"
                    value={category}
                    onChange={(e) => setCategory(e.target.value)}
                    fullWidth
                    sx={{ mb: 2 }}
                    helperText="e.g. tamil, hindi, american. Use 'american' or 'english' for Wikidata scraper."
                  />
                </>
              )}

              {/* Batch URLs Fields */}
              {jobType === 'batch_urls' && (
                <>
                  <TextField
                    label="URLs (one per line)"
                    value={batchUrls}
                    onChange={(e) => setBatchUrls(e.target.value)}
                    fullWidth
                    multiline
                    rows={6}
                    sx={{ mb: 2 }}
                    placeholder="https://en.wikipedia.org/wiki/Movie_1&#10;https://en.wikipedia.org/wiki/Movie_2"
                  />
                  <TextField
                    label="Category"
                    value={category}
                    onChange={(e) => setCategory(e.target.value)}
                    fullWidth
                    sx={{ mb: 2 }}
                    helperText="e.g. tamil, hindi, american"
                  />
                </>
              )}

              {/* Fix Lyricists Fields */}
              {jobType === 'fix_lyricists' && (
                <>
                  <FormControlLabel
                    control={<Checkbox checked={dryRun} onChange={(e) => setDryRun(e.target.checked)} />}
                    label="Dry Run (preview changes without saving)"
                    sx={{ mb: 2 }}
                  />
                  <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                    <TextField
                      label="Limit"
                      type="number"
                      value={fixLimit}
                      onChange={(e) => setFixLimit(e.target.value)}
                      helperText="Max movies to process"
                      size="small"
                      fullWidth
                    />
                    <TextField
                      label="Category"
                      value={category}
                      onChange={(e) => setCategory(e.target.value)}
                      helperText="e.g. tamil, hindi"
                      size="small"
                      fullWidth
                    />
                  </Box>
                </>
              )}

              <Button
                variant="contained"
                startIcon={creating ? <CircularProgress size={18} /> : <PlayArrowIcon />}
                onClick={handleCreateJob}
                disabled={creating}
                sx={{ mt: 1 }}
              >
                {creating ? 'Creating...' : 'Create Job'}
              </Button>
            </Box>
          </TabPanel>
        </CardContent>
      </Card>
    </Box>
  );
};
